
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Battery, BatteryLow, BrainCircuit, Clock, FastForward, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface CognitiveLoadTrackerProps {
  questionTimings: Record<string, number>;
  navigationPatterns: Array<{time: number, from: number, to: number}>;
  currentQuestionIndex: number;
  totalQuestions: number;
}

const CognitiveLoadTracker: React.FC<CognitiveLoadTrackerProps> = ({
  questionTimings,
  navigationPatterns,
  currentQuestionIndex,
  totalQuestions
}) => {
  // Analyze cognitive load based on time spent per question
  const analyzeTimings = () => {
    if (Object.keys(questionTimings).length < 5) return null;
    
    const timings = Object.values(questionTimings);
    const avgTime = timings.reduce((sum, time) => sum + time, 0) / timings.length;
    
    // Detect rush patterns (questions answered in less than 30% of average time)
    const rushThreshold = avgTime * 0.3;
    const rushQuestions = Object.entries(questionTimings)
      .filter(([_, time]) => time < rushThreshold)
      .map(([id]) => parseInt(id));
    
    // Detect fatigue patterns (questions taking more than 200% of average time)
    const fatigueThreshold = avgTime * 2;
    const fatigueQuestions = Object.entries(questionTimings)
      .filter(([_, time]) => time > fatigueThreshold)
      .map(([id]) => parseInt(id));
    
    // Check for consecutive fatigue questions to detect burnout zones
    let burnoutZones = [];
    let currentZone = [];
    
    for (let i = 0; i < fatigueQuestions.length; i++) {
      if (currentZone.length === 0 || fatigueQuestions[i] - fatigueQuestions[i-1] <= 3) {
        currentZone.push(fatigueQuestions[i]);
      } else {
        if (currentZone.length >= 2) {
          burnoutZones.push([...currentZone]);
        }
        currentZone = [fatigueQuestions[i]];
      }
    }
    
    if (currentZone.length >= 2) {
      burnoutZones.push(currentZone);
    }
    
    return {
      avgTime,
      rushQuestions,
      fatigueQuestions,
      burnoutZones
    };
  };
  
  // Analyze navigation behavior for erratic patterns
  const analyzeNavigation = () => {
    if (navigationPatterns.length < 5) return null;
    
    // Check for erratic navigation (jumping around questions)
    const jumpPatterns = navigationPatterns.filter((nav, idx) => {
      if (idx === 0) return false;
      const prevNav = navigationPatterns[idx - 1];
      return Math.abs(nav.to - prevNav.to) > 3; // Jump of more than 3 questions
    });
    
    // Check for rapid navigation switches
    const rapidSwitches = navigationPatterns.filter((nav, idx) => {
      if (idx === 0) return false;
      const prevNav = navigationPatterns[idx - 1];
      return (nav.time - prevNav.time) < 2000; // Less than 2 seconds between navigations
    });
    
    return {
      jumpPatterns,
      rapidSwitches
    };
  };
  
  // Calculate overall cognitive load score
  const calculateCognitiveLoad = (timingAnalysis, navigationAnalysis) => {
    if (!timingAnalysis || !navigationAnalysis) return 30; // Default value if not enough data
    
    let score = 50; // Start with a neutral score
    
    // Adjust based on fatigue questions
    if (timingAnalysis.fatigueQuestions.length > 0) {
      score += Math.min(timingAnalysis.fatigueQuestions.length * 3, 20);
    }
    
    // Adjust based on burnout zones
    if (timingAnalysis.burnoutZones.length > 0) {
      score += Math.min(timingAnalysis.burnoutZones.length * 5, 25);
    }
    
    // Adjust based on erratic navigation
    if (navigationAnalysis.jumpPatterns.length > 0) {
      score += Math.min(navigationAnalysis.jumpPatterns.length * 2, 15);
    }
    
    // Adjust based on rapid switches
    if (navigationAnalysis.rapidSwitches.length > 0) {
      score += Math.min(navigationAnalysis.rapidSwitches.length, 10);
    }
    
    return Math.min(score, 100); // Cap at 100
  };
  
  // Get insight messages based on analysis
  const getInsights = (timingAnalysis, navigationAnalysis, cognitiveLoad) => {
    if (!timingAnalysis || !navigationAnalysis) {
      return ["Continue answering more questions to receive cognitive insights."];
    }
    
    const insights = [];
    
    // Burnout zone insights
    if (timingAnalysis.burnoutZones.length > 0) {
      const mostRecentZone = timingAnalysis.burnoutZones[timingAnalysis.burnoutZones.length - 1];
      insights.push(`You started losing focus around Question ${mostRecentZone[0]} — recommended break point: Q${mostRecentZone[0] + 2}.`);
    }
    
    // Rush patterns insights
    if (timingAnalysis.rushQuestions.length > 5) {
      insights.push(`You rushed through ${timingAnalysis.rushQuestions.length} questions — check for potential mistakes.`);
    }
    
    // Navigation pattern insights
    if (navigationAnalysis.jumpPatterns.length > 5) {
      insights.push("Your frequent jumps between questions may indicate uncertainty or test anxiety.");
    }
    
    if (cognitiveLoad > 70) {
      insights.push("Consider taking a short 30-second break to reset your mental focus.");
    }
    
    if (insights.length === 0) {
      insights.push("You're maintaining good pace and focus. Keep it up!");
    }
    
    return insights;
  };
  
  const timingAnalysis = analyzeTimings();
  const navigationAnalysis = analyzeNavigation();
  const cognitiveLoad = calculateCognitiveLoad(timingAnalysis, navigationAnalysis);
  const insights = getInsights(timingAnalysis, navigationAnalysis, cognitiveLoad);
  
  return (
    <Card className="overflow-hidden shadow-sm mb-4">
      <CardContent className="p-4">
        <div className="flex items-center mb-3">
          <BrainCircuit className="h-5 w-5 text-indigo-500 mr-2" />
          <h3 className="font-medium text-indigo-700">Cognitive Load Monitor</h3>
        </div>
        
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-600">Mental Fatigue</span>
            <span className="text-sm font-medium">{cognitiveLoad}%</span>
          </div>
          <Progress 
            value={cognitiveLoad} 
            className="h-2"
            indicatorClassName={cn(
              cognitiveLoad < 40 ? "bg-green-500" :
              cognitiveLoad < 70 ? "bg-amber-500" :
              "bg-red-500"
            )}
          />
        </div>
        
        <div className="flex gap-2 mb-3">
          <Badge variant="outline" className={cn(
            "flex items-center gap-1.5",
            cognitiveLoad < 40 ? "bg-green-50 text-green-700 border-green-200" :
            cognitiveLoad < 70 ? "bg-amber-50 text-amber-700 border-amber-200" :
            "bg-red-50 text-red-700 border-red-200"
          )}>
            {cognitiveLoad < 40 ? (
              <Battery className="h-3.5 w-3.5" />
            ) : cognitiveLoad < 70 ? (
              <BatteryLow className="h-3.5 w-3.5" />
            ) : (
              <Zap className="h-3.5 w-3.5" />
            )}
            {cognitiveLoad < 40 ? "Focused" : 
             cognitiveLoad < 70 ? "Moderate Fatigue" : 
             "High Fatigue"}
          </Badge>
          
          {Object.keys(questionTimings).length > 0 && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Avg: {Math.round(Object.values(questionTimings).reduce((a, b) => a + b, 0) / Object.keys(questionTimings).length)}s/q
            </Badge>
          )}
          
          {navigationAnalysis?.rapidSwitches.length > 3 && (
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 flex items-center gap-1.5">
              <FastForward className="h-3.5 w-3.5" />
              Rapid Switches
            </Badge>
          )}
        </div>
        
        <div className="space-y-1">
          {insights.map((insight, index) => (
            <p key={index} className="text-sm text-gray-700 flex items-start">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-500 mt-1.5 mr-2 flex-shrink-0"></span>
              {insight}
            </p>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default CognitiveLoadTracker;
